import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PDFDownloadLink } from '@react-pdf/renderer';
import Avatar from '@/components/Avatar';
import ClientModal from '@/components/modals/ClientModal';
import InvoicePDF from '@/components/InvoicePDF';
import { I } from '@/lib/icons';
import { fmtEur, toIsoDate, addDays, fmtDate } from '@/lib/format';
import { useWorkspace, useClients, useInvoices, nextInvoiceNumber } from '@/hooks/useData';
import { useCloseOnWorkspaceSwitch } from '@/contexts/WorkspaceContext';
import { useToast } from '@/components/Toast';
import { DPH_RATES } from '@/lib/sections';
import { supabase } from '@/lib/supabase';

// HI-01: sessionStorage submission lock proti rapid double-click (per-tab).
// Pre cross-tab race + race medzi rovnakým userom v dvoch oknách stále hrozí
// duplicita FA čísla — to vyžaduje DB UNIQUE constraint na (workspace_id, number)
// + atomický increment cez RPC (viď docs/TODO.md, položka HI-01 follow-up).
const FA_SUBMIT_LOCK_KEY = 'cd-fa-submit-lock';
function acquireFaLock() {
  try {
    if (sessionStorage.getItem(FA_SUBMIT_LOCK_KEY)) return false;
    sessionStorage.setItem(FA_SUBMIT_LOCK_KEY, String(Date.now()));
    return true;
  } catch (_e) { return true; }
}
function releaseFaLock() {
  try { sessionStorage.removeItem(FA_SUBMIT_LOCK_KEY); } catch (_e) {}
}

export default function NewInvoiceScreen() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { workspace, refresh: refreshWorkspace } = useWorkspace();
  const { clients } = useClients(workspace?.id);
  const { invoices, create, update } = useInvoices(workspace?.id);
  const toast = useToast();

  // Stabilná referencia callbacku — bez useCallback je inline lambda nová pri každom
  // rendere a effect v useCloseOnWorkspaceSwitch by re-runoval donekonečna (CR-01).
  const onWorkspaceSwitch = useCallback(() => {
    toast.show?.('Firma bola prepnutá — návrat na zoznam faktúr.');
    navigate('/faktury', { replace: true });
  }, [toast, navigate]);

  useCloseOnWorkspaceSwitch(true, onWorkspaceSwitch);

  const editing = id ? invoices.find(x => x.id === id) : null;
  const [number, setNumber] = useState('');
  const [issueDate, setIssueDate] = useState(toIsoDate());
  const [deliveryDate, setDeliveryDate] = useState(toIsoDate());
  const [dueDate, setDueDate] = useState(toIsoDate(addDays(new Date(), 14)));
  const [vs, setVs] = useState('');
  const [clientId, setClientId] = useState(null);
  const [items, setItems] = useState([{ desc: '', qty: 1, unit: 'ks', price: 0, dph_rate: 0 }]);
  const [intro, setIntro] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('Bankový prevod');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [q, setQ] = useState('');
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  // hydrate when editing
  useEffect(() => {
    if (!hydrated && workspace) {
      if (editing) {
        setNumber(editing.number || '');
        setIssueDate(editing.issue_date || toIsoDate());
        setDeliveryDate(editing.delivery_date || editing.issue_date || toIsoDate());
        setDueDate(editing.due_date || toIsoDate(addDays(new Date(), 14)));
        setVs(editing.variable_symbol || editing.number || '');
        setClientId(editing.client_id || null);
        setItems(Array.isArray(editing.items) && editing.items.length ? editing.items : [{ desc: '', qty: 1, unit: 'ks', price: 0, dph_rate: 0 }]);
        setIntro(editing.intro || '');
        setPaymentMethod(editing.payment_method || 'Bankový prevod');
      } else {
        const auto = nextInvoiceNumber(workspace);
        setNumber(auto);
        setVs(auto);
        const dphRate = workspace?.default_dph_rate || 0;
        setItems([{ desc: '', qty: 1, unit: 'ks', price: 0, dph_rate: dphRate }]);
      }
      setHydrated(true);
    }
  }, [hydrated, workspace, editing]);

  const isDphPayer = !!workspace?.is_dph_payer;
  const subtotal = useMemo(() =>
    items.reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.price) || 0), 0),
  [items]);
  const dphAmount = useMemo(() => {
    if (!isDphPayer) return 0;
    return items.reduce((s, i) => {
      const line = (Number(i.qty) || 0) * (Number(i.price) || 0);
      const rate = Number(i.dph_rate) || 0;
      return s + line * rate / 100;
    }, 0);
  }, [items, isDphPayer]);
  const total = subtotal + dphAmount;

  const selectedClient = clients.find(c => c.id === clientId);

  const filtered = clients.filter(c => c.name.toLowerCase().includes(q.toLowerCase()));
  const pinned = filtered.filter(c => c.is_pinned);
  const others = filtered.filter(c => !c.is_pinned);

  const updateItem = (idx, patch) => {
    setItems(items.map((it, i) => i === idx ? { ...it, ...patch } : it));
  };
  const addItem = () => setItems([...items, { desc: '', qty: 1, unit: 'ks', price: 0, dph_rate: workspace?.default_dph_rate || 0 }]);
  const removeItem = (idx) => {
    if (items.length === 1) { setItems([{ desc: '', qty: 1, unit: 'ks', price: 0, dph_rate: 0 }]); return; }
    setItems(items.filter((_, i) => i !== idx));
  };

  const handleSave = async (status = 'issued') => {
    if (!clientId) { toast.error('Vyber klienta.'); return; }
    if (items.every(i => !i.desc?.trim())) { toast.error('Pridaj aspoň jednu položku.'); return; }
    if (!number.trim()) { toast.error('Zadaj číslo faktúry.'); return; }
    // HI-01: per-tab lock proti rapid double-click. Cross-tab race rieši DB
    // UNIQUE constraint (follow-up v docs/TODO.md).
    if (!editing && !acquireFaLock()) {
      toast.error('Faktúra sa už vytvára — počkaj prosím.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        client_id: clientId,
        number: number.trim(),
        variable_symbol: vs.trim() || number.trim(),
        status,
        issue_date: issueDate,
        delivery_date: deliveryDate || null,
        due_date: dueDate,
        items: items.map(it => ({
          desc: it.desc, qty: Number(it.qty) || 0, unit: it.unit || 'ks',
          price: Number(it.price) || 0, dph_rate: Number(it.dph_rate) || 0,
        })),
        subtotal, dph_amount: dphAmount, total,
        intro: intro || null,
        payment_method: paymentMethod,
        currency: 'EUR',
      };
      if (editing) {
        await update(editing.id, payload);
        toast.success('Faktúra uložená.');
      } else {
        // HI-01: optimistic concurrency na fa_counter — counter sa inkrementuje
        // IBA ak je v DB stále stará hodnota. Ak medzitým iný tab/zariadenie
        // counter zmenil, .eq() filter nezmatchuje, žiadny riadok sa neupdatne
        // a my vyhodíme chybu pred created invoice — žiadne duplicitné číslo.
        const expectedOld = workspace.fa_counter || 0;
        const { data: bumped, error: bumpErr } = await supabase
          .from('workspaces')
          .update({ fa_counter: expectedOld + 1 })
          .eq('id', workspace.id)
          .eq('fa_counter', expectedOld)
          .select()
          .single();
        if (bumpErr || !bumped) {
          throw new Error('Číslo faktúry bolo medzitým použité v inom okne. Skús to prosím znova.');
        }
        await create(payload);
        // Refresh workspace context aby ďalšia FA mala čerstvý fa_counter
        // (inak by 2. FA použila stale counter zo zoznamu firiem v contexte).
        await refreshWorkspace?.();
        toast.success('Faktúra vystavená.');
      }
      navigate('/faktury');
    } catch (e) { toast.error(e.message); }
    finally {
      setSaving(false);
      releaseFaLock();
    }
  };

  if (!workspace) return null;

  const previewInvoice = {
    number, variable_symbol: vs, issue_date: issueDate, delivery_date: deliveryDate,
    due_date: dueDate, items, subtotal, dph_amount: dphAmount, total,
    payment_method: paymentMethod, intro,
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, background: 'var(--bg)' }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 32px', borderBottom: '1px solid var(--border)', background: 'var(--bg-alt)', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <button onClick={() => navigate('/faktury')} style={{
            background: 'transparent', border: 'none', color: 'var(--text-muted)',
            display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, padding: 0, cursor: 'pointer',
            fontFamily: 'inherit',
          }}><I.chevL /> Späť</button>
          <div style={{ width: 1, height: 18, background: 'var(--border)' }} />
          <div>
            <div style={{
              fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase',
              letterSpacing: '0.07em', fontWeight: 700,
            }}>{editing ? 'EDITUJEM' : 'KONCEPT'}</div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>{editing ? `Úprava ${editing.number}` : 'Nová faktúra'}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {selectedClient && (
            <PDFDownloadLink
              document={<InvoicePDF invoice={previewInvoice} workspace={workspace} client={selectedClient} />}
              fileName={`FA-${number}.pdf`}
              className="cd-btn cd-btn-secondary" style={{ textDecoration: 'none' }}
            >
              {({ loading }) => (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  {loading ? <><span className="cd-spinner" /> PDF...</> : <><I.download /> Náhľad PDF</>}
                </span>
              )}
            </PDFDownloadLink>
          )}
          {!editing && (
            <button onClick={() => handleSave('draft')} disabled={saving}
              className="cd-btn cd-btn-secondary">
              <I.check /> Uložiť koncept
            </button>
          )}
          <button onClick={() => handleSave('issued')} disabled={saving}
            data-testid="issue-invoice" className="cd-btn cd-btn-primary">
            {saving ? <><span className="cd-spinner" style={{ borderTopColor: '#fff' }} /> Ukladám...</>
              : <><I.check /> {editing ? 'Uložiť zmeny' : 'Vystaviť faktúru'}</>}
          </button>
        </div>
      </div>

      {/* Paper */}
      <div className="cd-scroll" style={{
        flex: 1, overflowY: 'auto', background: 'rgba(0,0,0,0.04)',
        padding: '32px 32px 48px', display: 'flex', justifyContent: 'center',
      }}>
        <div style={{
          background: '#fff', width: 720, maxWidth: '100%',
          borderRadius: 'var(--r-md)',
          boxShadow: '0 6px 24px rgba(40,30,5,0.08), 0 1px 2px rgba(40,30,5,0.06)',
          padding: '36px 44px', fontFamily: 'var(--font)', color: 'var(--text-primary)',
        }}>
          {/* Title bar */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
            marginBottom: 28, paddingBottom: 14, borderBottom: '2px solid var(--text-primary)',
          }}>
            <div>
              <div style={{
                fontSize: 11, fontWeight: 700, color: 'var(--text-muted)',
                textTransform: 'uppercase', letterSpacing: '0.12em',
              }}>Faktúra</div>
              <input value={number} onChange={(e) => setNumber(e.target.value)}
                data-testid="invoice-number"
                style={{
                  background: 'transparent', border: 'none', borderBottom: '1px dashed transparent',
                  padding: '2px 4px', fontFamily: 'var(--font-mono)',
                  fontSize: 28, fontWeight: 700, outline: 'none', width: 220,
                  color: 'var(--text-primary)',
                }}
                onFocus={e => e.target.style.borderBottomColor = 'var(--product-primary)'}
                onBlur={e => e.target.style.borderBottomColor = 'transparent'} />
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{
                fontSize: 11, fontWeight: 700, color: 'var(--text-muted)',
                textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4,
              }}>Spolu</div>
              <div style={{
                fontSize: 32, fontWeight: 700, fontFamily: 'var(--font-display)',
                letterSpacing: '-0.025em', color: 'var(--product-primary-hover)', lineHeight: 1,
              }}>{fmtEur(total)}</div>
              {!isDphPayer && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                Nie som platca DPH
              </div>}
            </div>
          </div>

          {/* Sup + Customer */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 28, marginBottom: 28 }}>
            <div>
              <SmallLabel>Dodávateľ</SmallLabel>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>{workspace.name}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6 }}>
                {workspace.address}<br />
                IČO {workspace.ico}<br />
                {workspace.dic && <>DIČ {workspace.dic}<br /></>}
                {workspace.is_dph_payer && workspace.ic_dph && <>IČ DPH {workspace.ic_dph}<br /></>}
                {!workspace.is_dph_payer && 'Nie som platca DPH'}
              </div>
            </div>
            <div>
              <SmallLabel>Odberateľ</SmallLabel>
              {selectedClient ? (
                <div style={{ position: 'relative' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                    <Avatar name={selectedClient.name} size={28} />
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{selectedClient.name}</div>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6 }}>
                    {selectedClient.address}<br />
                    {selectedClient.ico && <>IČO {selectedClient.ico}<br /></>}
                    {selectedClient.dic && <>DIČ {selectedClient.dic}<br /></>}
                    {selectedClient.ic_dph && <>IČ DPH {selectedClient.ic_dph}</>}
                  </div>
                  <button onClick={() => { setClientId(null); setPickerOpen(false); }}
                    style={{
                      position: 'absolute', top: -2, right: -2, background: 'transparent', border: 'none',
                      color: 'var(--text-muted)', cursor: 'pointer', padding: 4,
                    }}><I.x /></button>
                </div>
              ) : (
                <div style={{ position: 'relative' }}>
                  <button onClick={() => setPickerOpen(o => !o)}
                    data-testid="select-client" style={{
                      width: '100%', padding: '14px 14px',
                      background: 'var(--product-primary-soft)',
                      border: '1px dashed var(--product-primary)',
                      borderRadius: 'var(--r-md)',
                      display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
                      textAlign: 'left', color: 'var(--product-primary-hover)',
                      fontWeight: 600, fontSize: 13, fontFamily: 'inherit',
                    }}><I.plus /> Vybrať klienta</button>
                  {pickerOpen && (
                    <div style={{
                      position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 50,
                      background: 'var(--bg-surface)', border: '1px solid var(--border)',
                      borderRadius: 'var(--r-md)', boxShadow: 'var(--sh-md)',
                      maxHeight: 320, overflowY: 'auto',
                    }}>
                      <div style={{
                        padding: '8px 10px', borderBottom: '1px solid var(--border)',
                        display: 'flex', alignItems: 'center', gap: 8, position: 'sticky', top: 0,
                        background: 'var(--bg-surface)',
                      }}>
                        <I.search />
                        <input value={q} onChange={(e) => setQ(e.target.value)}
                          placeholder="Hľadať klienta..." autoFocus
                          style={{
                            flex: 1, border: 'none', outline: 'none', background: 'transparent',
                            fontSize: 13, fontFamily: 'inherit',
                          }} />
                      </div>
                      {filtered.length === 0 && (
                        <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
                          {clients.length === 0 ? 'Zatiaľ žiadny klient. Pridaj prvého.' : 'Žiadne výsledky.'}
                        </div>
                      )}
                      {pinned.length > 0 && (
                        <>
                          <SmallSep>Pripnutí</SmallSep>
                          {pinned.map(c => <ClientOpt key={c.id} c={c} onClick={() => { setClientId(c.id); setPickerOpen(false); setQ(''); }} />)}
                        </>
                      )}
                      {others.length > 0 && (
                        <>
                          {pinned.length > 0 && <SmallSep>Všetci</SmallSep>}
                          {others.map(c => <ClientOpt key={c.id} c={c} onClick={() => { setClientId(c.id); setPickerOpen(false); setQ(''); }} />)}
                        </>
                      )}
                      <button onClick={() => { setPickerOpen(false); setClientModalOpen(true); }}
                        data-testid="add-client-inline" style={{
                          width: '100%', borderTop: '1px solid var(--border)', padding: '10px 12px',
                          color: 'var(--product-primary-hover)', fontSize: 12.5, fontWeight: 600,
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                          cursor: 'pointer', background: 'transparent', border: 'none', fontFamily: 'inherit',
                        }}><I.plus /> Nový klient</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Dates */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14,
            padding: '14px 16px', background: 'var(--bg-input)',
            borderRadius: 'var(--r-md)', marginBottom: 22,
          }}>
            <PaperField label="Vystavená">
              <input className="cd-input mono" type="date" value={issueDate}
                onChange={e => setIssueDate(e.target.value)}
                style={{ background: 'transparent', border: 'none', padding: 2, fontSize: 13 }} />
            </PaperField>
            <PaperField label="Dodanie">
              <input className="cd-input mono" type="date" value={deliveryDate}
                onChange={e => setDeliveryDate(e.target.value)}
                style={{ background: 'transparent', border: 'none', padding: 2, fontSize: 13 }} />
            </PaperField>
            <PaperField label="Splatnosť">
              <input className="cd-input mono" type="date" value={dueDate}
                onChange={e => setDueDate(e.target.value)}
                data-testid="due-date"
                style={{ background: 'transparent', border: 'none', padding: 2, fontSize: 13, color: 'var(--product-primary-hover)' }} />
            </PaperField>
            <PaperField label="VS">
              <input value={vs} onChange={e => setVs(e.target.value)}
                style={{
                  background: 'transparent', border: 'none', padding: 2, fontSize: 13,
                  fontFamily: 'var(--font-mono)', outline: 'none', width: '100%',
                }} />
            </PaperField>
          </div>

          {/* Bank/payment */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14, marginBottom: 22 }}>
            <PaperField label="IBAN">
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, padding: 2 }}>{workspace.iban || '—'}</div>
            </PaperField>
            <PaperField label="Spôsob úhrady">
              <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}
                style={{
                  background: 'transparent', border: 'none', padding: 2, fontSize: 13,
                  outline: 'none', fontFamily: 'inherit', width: '100%',
                }}>
                <option>Bankový prevod</option>
                <option>Karta</option>
                <option>Hotovosť</option>
              </select>
            </PaperField>
          </div>

          {/* Intro */}
          <div style={{ marginBottom: 18 }}>
            <input value={intro} onChange={(e) => setIntro(e.target.value)}
              placeholder="Úvodný text (voliteľné)..."
              style={{
                width: '100%', padding: '8px 0', background: 'transparent',
                border: 'none', borderBottom: '1px dashed var(--border)',
                outline: 'none', fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'inherit',
              }} />
          </div>

          {/* Items */}
          <div style={{
            border: '1px solid var(--border)', borderRadius: 'var(--r-md)',
            overflow: 'hidden', marginBottom: 22,
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isDphPayer
                ? '24px 1fr 60px 50px 90px 60px 100px 28px'
                : '24px 1fr 60px 50px 100px 110px 28px',
              gap: 6, padding: '9px 10px',
              fontSize: 10, fontWeight: 700, color: 'var(--text-muted)',
              letterSpacing: '0.07em', textTransform: 'uppercase',
              borderBottom: '1px solid var(--border)', background: 'var(--bg-input)',
            }}>
              <div>#</div>
              <div>Názov</div>
              <div style={{ textAlign: 'right' }}>Počet</div>
              <div style={{ textAlign: 'center' }}>MJ</div>
              <div style={{ textAlign: 'right' }}>Cena</div>
              {isDphPayer && <div style={{ textAlign: 'center' }}>DPH</div>}
              <div style={{ textAlign: 'right' }}>Spolu</div>
              <div></div>
            </div>
            {items.map((item, idx) => (
              <ItemRow key={idx} item={item} idx={idx} last={idx === items.length - 1}
                isDphPayer={isDphPayer}
                onChange={(patch) => updateItem(idx, patch)}
                onRemove={() => removeItem(idx)} />
            ))}
            <button onClick={addItem} data-testid="add-item"
              style={{
                width: '100%', padding: '10px 12px', background: 'transparent',
                border: 'none', borderTop: '1px dashed var(--border)',
                color: 'var(--product-primary-hover)', fontSize: 12.5, fontWeight: 600,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                cursor: 'pointer', fontFamily: 'inherit',
              }}><I.plus /> Pridať položku</button>
          </div>

          {/* Totals */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 24 }}>
            <div style={{ width: 280 }}>
              <SumLine label="Suma bez DPH" value={fmtEur(subtotal)} />
              <SumLine label={isDphPayer ? 'DPH' : 'DPH (oslobodenie §4)'} value={isDphPayer ? fmtEur(dphAmount) : '—'} />
              <div style={{ height: 1, background: 'var(--text-primary)', margin: '8px 0' }} />
              <SumLine label="Spolu k úhrade" value={fmtEur(total)} bold size={18} accent />
            </div>
          </div>

          <div style={{
            fontSize: 11, color: 'var(--text-muted)', textAlign: 'center',
            paddingTop: 14, borderTop: '1px solid var(--border)',
          }}>
            {workspace.notice || 'Vystavené v podnikai.sk · ďakujeme za včasnú úhradu.'}
          </div>
        </div>
      </div>

      <ClientModal
        open={clientModalOpen}
        client={null}
        onClose={() => setClientModalOpen(false)}
      />
    </div>
  );
}

function PaperField({ label, children }) {
  return (
    <div>
      <div style={{
        fontSize: 9.5, fontWeight: 700, color: 'var(--text-muted)',
        textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 3,
      }}>{label}</div>
      {children}
    </div>
  );
}
function SumLine({ label, value, bold, size = 13, accent }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
      padding: '4px 0', fontSize: size,
      fontWeight: bold ? 700 : 500,
      color: accent ? 'var(--product-primary-hover)' : 'var(--text-primary)',
    }}>
      <span style={{ color: accent ? 'inherit' : 'var(--text-muted)', fontWeight: 500 }}>{label}</span>
      <span className="tabnum" style={{
        fontFamily: bold ? 'var(--font-display)' : 'var(--font-mono)',
        letterSpacing: bold ? '-0.01em' : 0,
      }}>{value}</span>
    </div>
  );
}
function SmallLabel({ children }) {
  return (
    <div style={{
      fontSize: 10, fontWeight: 700, color: 'var(--text-muted)',
      textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8,
    }}>{children}</div>
  );
}
function SmallSep({ children }) {
  return (
    <div style={{
      fontSize: 10, fontWeight: 700, color: 'var(--text-muted)',
      padding: '8px 12px 4px', letterSpacing: '0.07em', textTransform: 'uppercase',
    }}>{children}</div>
  );
}
function ClientOpt({ c, onClick }) {
  return (
    <button onClick={onClick} data-testid={`pick-client-${c.id}`}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 10,
        padding: '8px 12px', background: 'transparent', border: 'none', textAlign: 'left',
        cursor: 'pointer', fontFamily: 'inherit',
      }}
      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
      <Avatar name={c.name} size={26} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500 }}>{c.name}</div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{c.ico ? `IČO ${c.ico}` : 'bez IČO'}</div>
      </div>
      {c.is_pinned && <span style={{ color: 'var(--sec-orange)' }}><I.pin /></span>}
    </button>
  );
}
function ItemRow({ item, idx, last, isDphPayer, onChange, onRemove }) {
  const total = (Number(item.qty) || 0) * (Number(item.price) || 0);
  const cellInput = {
    background: 'transparent', border: 'none', borderBottom: '1px dashed transparent',
    padding: '2px 4px', outline: 'none', fontSize: 13, fontFamily: 'inherit', width: '100%',
    color: 'var(--text-primary)',
  };
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: isDphPayer
        ? '24px 1fr 60px 50px 90px 60px 100px 28px'
        : '24px 1fr 60px 50px 100px 110px 28px',
      gap: 6, padding: '8px 10px', alignItems: 'center',
      borderBottom: last ? 'none' : '1px solid var(--border)',
      fontSize: 13,
    }}>
      <div style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>{idx + 1}</div>
      <input value={item.desc || ''} onChange={e => onChange({ desc: e.target.value })}
        placeholder="Názov položky" data-testid={`item-desc-${idx}`}
        style={{ ...cellInput, fontWeight: 500 }} />
      <input value={item.qty} onChange={e => onChange({ qty: e.target.value })}
        placeholder="1" inputMode="decimal"
        data-testid={`item-qty-${idx}`}
        style={{ ...cellInput, textAlign: 'right', fontFamily: 'var(--font-mono)' }} />
      <input value={item.unit || 'ks'} onChange={e => onChange({ unit: e.target.value })}
        placeholder="ks" style={{ ...cellInput, textAlign: 'center' }} />
      <input value={item.price} onChange={e => onChange({ price: e.target.value })}
        placeholder="0.00" inputMode="decimal"
        data-testid={`item-price-${idx}`}
        style={{ ...cellInput, textAlign: 'right', fontFamily: 'var(--font-mono)' }} />
      {isDphPayer && (
        <select value={item.dph_rate || 0} onChange={e => onChange({ dph_rate: Number(e.target.value) })}
          style={{ ...cellInput, textAlign: 'center', fontFamily: 'var(--font-mono)' }}>
          {DPH_RATES.map(r => <option key={r} value={r}>{r}%</option>)}
        </select>
      )}
      <div className="tabnum" style={{ textAlign: 'right', fontWeight: 600, padding: '0 4px' }}>
        {fmtEur(total)}
      </div>
      <button onClick={onRemove} data-testid={`item-remove-${idx}`}
        style={{
          background: 'transparent', border: 'none', color: 'var(--text-muted)', padding: 0,
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}><I.x /></button>
    </div>
  );
}
